import { retrieveAssignmentApps } from "@/lib/data/requests/matters/matter-task";
// #region --------------- Matter Task React Query hooks ----------------------

import {
  addMatterTaskUserAssignmentsV2,
  createMatterTaskV3,
  removeMatterTaskUserAssignmentsV2,
  deleteMatterTasksV1,
  deleteMatterTaskV1,
  retrieveMatterTasksV2,
  retrieveUserTasksV2,
  type RetrieveUserTasksParams,
  updateMatterTasksV1,
  updateMatterTaskV1,
} from "@/lib/data/requests";
import { invalidateAnnotationQueries } from "@/lib/data/react-query/annotation-query-utils";
import { queryKeys } from "@/lib/data/static";
import { type APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  type QueryClient,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

const invalidateTaskQueries = async (
  queryClient: QueryClient,
  variables: { matterId?: string; massTortId?: string } | undefined,
) => {
  await invalidateAnnotationQueries(queryClient, variables);
  if (variables?.matterId)
    await queryClient.invalidateQueries({
      queryKey: [queryKeys.matterPhases, variables.matterId],
    });
};

export const getUserTasksOptions = ({
  enabled = true,
  ...params
}: RetrieveUserTasksParams & { enabled?: boolean } = {}) => {
  return {
    queryKey: [queryKeys.matters, "user-tasks", params],
    queryFn: () => retrieveUserTasksV2(params),
    enabled,
  };
};

export const retrieveMatterTasksQueryOptions = (
  data: Parameters<typeof retrieveMatterTasksV2>[0],
) => ({
  queryKey: [queryKeys.matters, data.matterId, "tasks", JSON.stringify(data)],
  enabled: Boolean(data.matterId),
  queryFn: () => retrieveMatterTasksV2(data),
});

export const useInfiniteMatterTasks = (
  data: Omit<Parameters<typeof retrieveMatterTasksV2>[0], "pageNumber">,
) =>
  useInfiniteQuery({
    queryKey: [queryKeys.matters, data.matterId, "tasks", "infinite", data],
    enabled: Boolean(data.matterId),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      retrieveMatterTasksV2({ ...data, pageNumber: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      const pageNumber = lastPage.pagination?.pageNumber ?? allPages.length;
      const pageSize = lastPage.pagination?.pageSize ?? data.pageSize ?? 25;
      const count = lastPage.pagination?.count ?? 0;
      return pageNumber * pageSize < count ? pageNumber + 1 : undefined;
    },
  });

export const useInfiniteUserTasks = (
  data: Omit<RetrieveUserTasksParams, "pageNumber"> & {
    enabled?: boolean;
  },
) => {
  const { enabled = true, ...params } = data;

  return useInfiniteQuery({
    queryKey: [queryKeys.matters, "user-tasks", "infinite", params],
    enabled,
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam }) =>
      retrieveUserTasksV2({ ...params, pageNumber: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      const pageNumber = lastPage.pagination?.pageNumber ?? allPages.length;
      const pageSize = lastPage.pagination?.pageSize ?? params.pageSize ?? 25;
      const count = lastPage.pagination?.count ?? 0;
      return pageNumber * pageSize < count ? pageNumber + 1 : undefined;
    },
  });
};

export const useCreateMatterTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterTaskV3>[0]) =>
      createMatterTaskV3(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating matter task");
    },
    onSettled: (_data, _error, variables) => {
      void invalidateTaskQueries(queryClient, variables);
    },
  });
};

export const useUpdateMatterTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterTaskV1>[0]) =>
      updateMatterTaskV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating matter task");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateTaskQueries(queryClient, variables);
    },
    onSuccess: () => {
      toast.success("Matter task updated successfully");
    },
  });
};

export const useUpdateMatterTasks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterTasksV1>[0]) =>
      updateMatterTasksV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating matter tasks");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateTaskQueries(queryClient, variables[0]);
    },
    onSuccess: (_data, variables) => {
      toast.success(
        variables.length === 1
          ? "Matter task successfully updated"
          : `Updated ${variables.length} matter tasks successfully`,
      );
    },
  });
};

export const useAddMatterTaskUserAssignments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof addMatterTaskUserAssignmentsV2>[0]) =>
      addMatterTaskUserAssignmentsV2(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error adding task assignment");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.matters] });
    },
    onSuccess: ({ data }) => {
      const assignmentLabel = data.role.toLowerCase();
      const recipient = data.integrationAppId ? "App" : "User";
      if (data.changedTaskIds.length === 0) {
        toast.success(
          `${recipient} is already an ${assignmentLabel} on the selected task${
            data.taskIds.length === 1 ? "" : "s"
          }`,
        );
        return;
      }

      toast.success(
        `Added ${recipient.toLowerCase()} as ${assignmentLabel} to ${data.changedTaskIds.length} task${
          data.changedTaskIds.length === 1 ? "" : "s"
        }`,
      );
    },
  });
};

export const useRemoveMatterTaskUserAssignments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof removeMatterTaskUserAssignmentsV2>[0],
    ) => removeMatterTaskUserAssignmentsV2(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error removing task assignment");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.matters] });
    },
    onSuccess: ({ data }) => {
      const assignmentLabel = data.role.toLowerCase();
      const recipient = data.integrationAppId ? "App" : "User";
      if (data.changedTaskIds.length === 0) {
        toast.success(
          `${recipient} is not an ${assignmentLabel} on the selected task${
            data.taskIds.length === 1 ? "" : "s"
          }`,
        );
        return;
      }

      toast.success(
        `Removed ${recipient.toLowerCase()} as ${assignmentLabel} from ${data.changedTaskIds.length} task${
          data.changedTaskIds.length === 1 ? "" : "s"
        }`,
      );
    },
  });
};

export const useDeleteMatterTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterTaskV1>[0]) =>
      deleteMatterTaskV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting matter task");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateTaskQueries(queryClient, variables);
    },
    onSuccess: () => {
      toast.success("Matter task deleted successfully");
    },
  });
};

export const useDeleteMatterTasks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterTasksV1>[0]) =>
      deleteMatterTasksV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting matter tasks");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateTaskQueries(queryClient, variables[0]);
    },
    onSuccess: (_data, variables) => {
      toast.success(`Deleted ${variables.length} matter tasks successfully`);
    },
  });
};

export const getAssignmentAppsOptions = (enabled = true) => ({
  queryKey: [queryKeys.matters, "assignment-apps"],
  queryFn: retrieveAssignmentApps,
  enabled,
  staleTime: 30_000,
});
